import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  RoomCapabilities,
  RoomHeader as RoomHeaderData,
} from '../../../generated/api/chat-room'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { AvatarInner } from '../../common/Avatar'
import { IconChoices } from '../../common/Icon'
import { UserListItem } from '../../common/UserListItem/UserListItem'
import { hasRoomSettings } from './Settings/roomCapabilities'

const AVATAR_PLACEHOLDER = {
  user: 'avatar',
  interests: 'interests',
  society: 'society',
} as const

// Keeps an empty text line at full height so the header never changes size.
const EMPTY_LINE = ' '

type RoomIdentity = Pick<
  RoomHeaderData,
  'type' | 'title' | 'avatar' | 'otherUserId' | 'societyId'
> & { societyName?: string }

interface RoomHeaderProps {
  identity?: RoomIdentity
  capabilities?: RoomCapabilities
  onToggleSettings: () => void
}

export const RoomHeader = ({
  identity,
  capabilities,
  onToggleSettings,
}: RoomHeaderProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const navigate = useNavigate()

  if (!identity) {
    return (
      <div className="flex-none">
        <UserListItem
          avatar={null}
          name={EMPTY_LINE}
          text={EMPTY_LINE}
          className="p-4 md:py-4 md:px-6"
        />
      </div>
    )
  }

  const title =
    identity.type === 'oneonone' && !identity.otherUserId
      ? translate('common.user.deletedUserName')
      : identity.title

  const onProfileClick = identity.otherUserId
    ? (): void =>
        navigate(
          reverseUrl('user:society-detail', {
            id: identity.otherUserId,
            societyId: identity.societyId,
          })
        )
    : undefined

  return (
    <div className="flex-none">
      <UserListItem
        avatar={
          <AvatarInner
            alt={`${title} avatar`}
            url={identity.avatar.url}
            initials={identity.avatar.initials}
            placeholder={AVATAR_PLACEHOLDER[identity.avatar.kind]}
            onClick={onProfileClick}
          />
        }
        name={title}
        text={identity.societyName || EMPTY_LINE}
        icon={
          capabilities && hasRoomSettings(capabilities)
            ? IconChoices.MORE
            : undefined
        }
        onIconClick={onToggleSettings}
        iconTestId="room-settings-button"
        className="p-4 md:py-4 md:px-6"
      />
    </div>
  )
}
